import BaseQuery from "@/stateManagement/BaseQuery";
import AttributesEntity from "@apps/reviews/entities/AttributesEntity";
import { AttributesDateValue } from "@apps/reviews/types";
import { BehaviorSubject, map } from "rxjs";
import { injectable } from "tsyringe";
import * as Yup from "yup";

import { ATTRIBUTE_TYPE } from "../../../constants";
import AttributesStore, { AttributesState } from "../stores/AttributesStore";

export type AttributesFormSchema = { [key: string]: Yup.AnySchema };
export type AttributesFormInitialValues = {
    [key: string]: string | string[] | null | AttributesDateValue;
};

@injectable()
export default class AttributesQuery extends BaseQuery<AttributesState> {
    active$ = new BehaviorSubject<AttributesEntity | undefined>(undefined);
    all$ = this.select((state) => [
        ...state.attributes.customerAttributes,
        ...state.attributes.productAttributes,
    ]);
    attributes$ = this.select((state) => state.attributes);

    schema$ = this.attributes$.pipe(
        map(({ customerAttributes, productAttributes }) => [
            ...customerAttributes,
            ...productAttributes,
        ])
    );

    formData$ = this.attributes$.pipe(
        map((attributes) => {
            const schema: AttributesFormSchema = {};
            const initialValues: AttributesFormInitialValues = {};

            [
                ...attributes.customerAttributes,
                ...attributes.productAttributes,
            ].forEach((a) => {
                if (
                    [
                        ATTRIBUTE_TYPE.TEXTFIELD,
                        ATTRIBUTE_TYPE.SINGLE_VALUE,
                        ATTRIBUTE_TYPE.LOCATION,
                    ].includes(a.content.type)
                ) {
                    schema[a.id] = a.isRequired
                        ? Yup.string().required("validation_field_required")
                        : Yup.string();
                    initialValues[a.id] = "";
                }

                if (a.content.type === ATTRIBUTE_TYPE.MULTI_VALUE) {
                    schema[a.id] = a.isRequired
                        ? Yup.array()
                              .of(Yup.string())
                              .min(1, "validation_field_required")
                              .required("validation_field_required")
                        : Yup.array().of(Yup.string());
                    initialValues[a.id] = [];
                }

                if (a.content.type === ATTRIBUTE_TYPE.DATE) {
                    schema[a.id] = a.isRequired
                        ? Yup.object({
                              day: Yup.number().nullable(),
                              month: Yup.number().nullable(),
                              year: Yup.number().nullable(),
                          })
                              .required("validation_field_required")
                              .test(
                                  "has-some-value",
                                  "validation_field_required",
                                  (value: any) =>
                                      value
                                          ? ["day", "month", "year"].some(
                                                (key) =>
                                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                                    value[key] !== null
                                            )
                                          : false
                              )
                        : Yup.object({
                              day: Yup.number().nullable(),
                              month: Yup.number().nullable(),
                              year: Yup.number().nullable(),
                          });
                    initialValues[a.id] = {
                        day: null,
                        month: null,
                        year: null,
                    };
                }

                if (
                    [
                        ATTRIBUTE_TYPE.RANGE,
                        ATTRIBUTE_TYPE.CENTERED_RANGE,
                    ].includes(a.content.type)
                ) {
                    schema[a.id] = a.isRequired
                        ? Yup.number()
                              .typeError("validation_field_required")
                              .required("validation_field_required")
                        : Yup.number().nullable();
                    initialValues[a.id] = null;
                }
            });

            return {
                schema,
                initialValues,
            };
        })
    );

    formSchema$ = this.formData$.pipe(map((data) => data.schema));
    formInitialValues$ = this.formData$.pipe(map((data) => data.initialValues));

    hasAttributes$ = this.attributes$.pipe(
        map((attributes) => {
            return !![
                ...attributes.customerAttributes,
                ...attributes.productAttributes,
            ].length;
        })
    );

    constructor(protected store: AttributesStore) {
        super(store);
    }
}
